import { useEffect, useState, type FormEvent } from "react";
import { PageHeader, Panel } from "../components/Surface";
import { Button } from "../components/ui/Button";
import { inputClass } from "../components/FormKit";
import { listApiKeys, createApiKey, revokeApiKey, type ApiKey } from "../lib/integration";

export function Integrations() {
  const [keys, setKeys] = useState<ApiKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState<string | null>(null);
  const [freshKey, setFreshKey] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  async function refresh() { setKeys((await listApiKeys()).keys); }
  useEffect(() => { refresh().finally(() => setLoading(false)); }, []);

  async function create(e: FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    setBusy("new");
    try {
      const { key } = await createApiKey(name.trim());
      setFreshKey(key);
      setName("");
      await refresh();
    } finally { setBusy(null); }
  }

  async function revoke(k: ApiKey) {
    setBusy(k.id);
    try { await revokeApiKey(k.id); await refresh(); } finally { setBusy(null); }
  }

  async function copy() {
    if (!freshKey) return;
    try { await navigator.clipboard.writeText(freshKey); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch { /* selectable */ }
  }

  return (
    <div className="mx-auto grid w-full max-w-2xl gap-6">
      <PageHeader title="API keys" subtitle="Let external systems act on your behalf. Keys are shown once — store them safely." />

      {freshKey && (
        <Panel title="Your new API key">
          <p className="text-sm text-muted">Copy this now — for your security it won't be shown again.</p>
          <div className="flex flex-wrap items-center gap-2">
            <input className={`${inputClass} flex-1 font-mono text-xs`} readOnly value={freshKey} aria-label="New API key" onFocus={(e) => e.currentTarget.select()} data-testid="fresh-key" />
            <Button onClick={copy}>{copied ? "Copied ✓" : "Copy"}</Button>
          </div>
          <button type="button" className="justify-self-start font-mono text-[11px] text-muted hover:underline" onClick={() => setFreshKey(null)}>Done</button>
        </Panel>
      )}

      <Panel title="Create a key">
        <form className="flex flex-wrap items-end gap-2" onSubmit={create}>
          <input className={`${inputClass} flex-1`} placeholder="Key name (e.g. Zapier, internal tool)" value={name} onChange={(e) => setName(e.target.value)} required aria-label="Key name" />
          <Button type="submit" disabled={busy === "new" || !name.trim()}>Create key</Button>
        </form>
      </Panel>

      {loading ? (
        <p className="font-mono text-sm text-muted">Loading…</p>
      ) : keys.length === 0 ? (
        <p className="font-mono text-sm text-muted">No API keys yet.</p>
      ) : (
        <Panel title={`Keys (${keys.length})`}>
          <ul className="grid gap-2">
            {keys.map((k) => (
              <li key={k.id} className="flex items-center justify-between gap-3 border-b border-line pb-2 text-sm last:border-0 last:pb-0">
                <div className="grid gap-0.5">
                  <span className="font-bold">{k.name}</span>
                  <span className="font-mono text-[11px] text-muted">
                    {k.prefix}…{k.revoked_at ? " · revoked" : k.last_used_at ? ` · last used ${new Date(k.last_used_at).toLocaleDateString()}` : " · never used"}
                  </span>
                </div>
                {!k.revoked_at && (
                  <Button variant="ghost" disabled={busy === k.id} onClick={() => revoke(k)}>Revoke</Button>
                )}
              </li>
            ))}
          </ul>
        </Panel>
      )}
    </div>
  );
}
